import { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Switch,
  Table,
  Tag,
  Upload,
  UploadFile,
} from "antd";
import { UploadProps } from "antd/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileContract,
  faFileInvoice,
  faGear,
  faLock,
  faLockOpen,
  faPlus,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import { Management } from "../../api/Management";
import {
  ManagedStructure,
  Payment,
  PaymentStatus,
} from "../../types/Payment";
import { useToasts } from "../../contexts/toasts";
import dayjs from "dayjs";
import PlatformLockCard from "./PlatformLockCard";

// Formatta una data (ISO o YYYY-MM-DD) nel formato dd/mm/YYYY.
function formatDate(value?: string) {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : value;
}

function formatEuro(v?: number | null) {
  return v != null ? `€ ${v.toFixed(2)}` : "-";
}

function paymentsTotal(list?: Payment[]) {
  return (list || []).reduce((s, p) => s + (p.amount || 0), 0);
}

function statusTag(status: PaymentStatus) {
  return status === PaymentStatus.Paid ? (
    <Tag color="green">Pagato</Tag>
  ) : (
    <Tag color="orange">In attesa</Tag>
  );
}

export default function StructuresPayments() {
  const { showToast } = useToasts();
  const [loading, setLoading] = useState(true);
  const [structures, setStructures] = useState<ManagedStructure[]>([]);

  // Drawer "gestisci struttura"
  const [active, setActive] = useState<ManagedStructure | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [savingContract, setSavingContract] = useState(false);
  const [annualFeeInput, setAnnualFeeInput] = useState<string>("");
  const [savingFee, setSavingFee] = useState(false);

  // Blocco del singolo circolo
  const [lockTarget, setLockTarget] = useState<ManagedStructure | null>(null);
  const [lockMessage, setLockMessage] = useState("");
  const [savingLockId, setSavingLockId] = useState<string | null>(null);

  // Modal "segna pagamento"
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [form] = Form.useForm();

  const loadStructures = useCallback(() => {
    setLoading(true);
    Management.getStructures().then(
      ({ data }) => {
        setStructures(data.list);
        setLoading(false);
      },
      () => {
        setLoading(false);
        showToast(
          "structures-load-error",
          "Errore nel reperimento delle strutture.",
          "",
          "danger"
        );
      }
    );
  }, [showToast]);

  useEffect(() => {
    loadStructures();
  }, [loadStructures]);

  const loadPayments = useCallback((structureId: string) => {
    setPaymentsLoading(true);
    Management.getPayments(structureId).then(
      ({ data }) => {
        setPayments(data.list);
        setPaymentsLoading(false);
      },
      () => setPaymentsLoading(false)
    );
  }, []);

  const openDrawer = useCallback(
    (structure: ManagedStructure) => {
      setActive(structure);
      setContractFile(null);
      setAnnualFeeInput(
        structure.annualFee != null ? String(structure.annualFee) : ""
      );
      loadPayments(structure.id);
    },
    [loadPayments]
  );

  const closeDrawer = useCallback(() => {
    setActive(null);
    setPayments([]);
  }, []);

  const handleSetLock = useCallback(
    (structure: ManagedStructure, locked: boolean, message?: string) => {
      setSavingLockId(structure.id);
      Management.setStructureLock(structure.id, locked, message).then(
        ({ data }) => {
          setStructures((prev) =>
            prev.map((s) =>
              s.id === structure.id
                ? { ...s, locked: data.locked, lockMessage: data.lockMessage }
                : s
            )
          );
          setSavingLockId(null);
          setLockTarget(null);
          setLockMessage("");
          showToast(
            "structure-lock-success",
            locked
              ? `${structure.name} bloccato.`
              : `${structure.name} sbloccato.`,
            "",
            "success"
          );
        },
        () => {
          setSavingLockId(null);
          showToast(
            "structure-lock-error",
            "Errore nell'aggiornamento del blocco del circolo.",
            "",
            "danger"
          );
        }
      );
    },
    [showToast]
  );

  const handleToggleActive = useCallback(
    (structure: ManagedStructure, value: boolean) => {
      Management.setStructureActive(structure.id, value).then(
        () => {
          setStructures((prev) =>
            prev.map((s) =>
              s.id === structure.id ? { ...s, active: value } : s
            )
          );
          setActive((prev) =>
            prev && prev.id === structure.id
              ? { ...prev, active: value }
              : prev
          );
          showToast(
            "structure-active-success",
            value ? "Struttura abilitata." : "Struttura disabilitata.",
            "",
            "success"
          );
        },
        () =>
          showToast(
            "structure-active-error",
            "Errore nell'aggiornamento dello stato della struttura.",
            "",
            "danger"
          )
      );
    },
    [showToast]
  );

  const openDocument = useCallback(
    (loader: Promise<{ data: { url: string } }>) => {
      loader.then(
        ({ data }) => window.open(data.url, "_blank"),
        () =>
          showToast(
            "document-error",
            "Documento non disponibile.",
            "",
            "danger"
          )
      );
    },
    [showToast]
  );

  const handleUploadContract = useCallback(() => {
    if (!active || !contractFile) return;
    const fd = new FormData();
    fd.append("document", contractFile);
    setSavingContract(true);
    Management.uploadContract(active.id, fd).then(
      ({ data }) => {
        setSavingContract(false);
        setContractFile(null);
        setActive((prev) =>
          prev ? { ...prev, contractUrl: data.structure.contractUrl } : prev
        );
        setStructures((prev) =>
          prev.map((s) =>
            s.id === active.id
              ? { ...s, contractUrl: data.structure.contractUrl }
              : s
          )
        );
        showToast(
          "contract-upload-success",
          "Contratto caricato correttamente.",
          "",
          "success"
        );
      },
      () => {
        setSavingContract(false);
        showToast(
          "contract-upload-error",
          "Errore nel caricamento del contratto.",
          "",
          "danger"
        );
      }
    );
  }, [active, contractFile, showToast]);

  const handleSaveAnnualFee = useCallback(() => {
    if (!active) return;
    const fee = annualFeeInput.trim() === "" ? null : +annualFeeInput;
    if (fee != null && (isNaN(fee) || fee < 0)) {
      showToast("fee-invalid", "Importo non valido.", "", "danger");
      return;
    }
    setSavingFee(true);
    Management.setStructureAnnualFee(active.id, fee).then(
      () => {
        setSavingFee(false);
        setActive((prev) =>
          prev ? { ...prev, annualFee: fee ?? undefined } : prev
        );
        loadPayments(active.id);
        loadStructures();
        showToast(
          "fee-save-success",
          "Costo abbonamento aggiornato.",
          "",
          "success"
        );
      },
      () => {
        setSavingFee(false);
        showToast(
          "fee-save-error",
          "Errore nell'aggiornamento del costo abbonamento.",
          "",
          "danger"
        );
      }
    );
  }, [active, annualFeeInput, loadPayments, loadStructures, showToast]);

  const handleCreatePayment = useCallback(
    (values: {
      amount?: string;
      paymentDate: string;
      period?: string;
      isDeposit?: boolean;
      note?: string;
    }) => {
      if (!active) return;
      const fd = new FormData();
      fd.append("structureId", active.id);
      fd.append("paymentDate", values.paymentDate);
      fd.append("isDeposit", values.isDeposit ? "true" : "false");
      if (values.amount) fd.append("amount", values.amount);
      if (values.period) fd.append("period", values.period);
      if (values.note) fd.append("note", values.note);
      if (invoiceFile) fd.append("document", invoiceFile);

      setSavingPayment(true);
      Management.createPayment(fd).then(
        () => {
          setSavingPayment(false);
          setPaymentModalOpen(false);
          setInvoiceFile(null);
          form.resetFields();
          loadPayments(active.id);
          loadStructures();
          showToast(
            "payment-create-success",
            "Pagamento registrato correttamente.",
            "",
            "success"
          );
        },
        () => {
          setSavingPayment(false);
          showToast(
            "payment-create-error",
            "Errore nella registrazione del pagamento.",
            "",
            "danger"
          );
        }
      );
    },
    [active, invoiceFile, form, loadPayments, loadStructures, showToast]
  );

  const handleDeletePayment = useCallback(
    (payment: Payment) => {
      Modal.confirm({
        title: "Eliminare il pagamento?",
        content: "L'operazione non è reversibile.",
        okText: "Elimina",
        okButtonProps: { danger: true },
        cancelText: "Annulla",
        onOk: () =>
          Management.deletePayment(payment.id).then(
            () => {
              if (active) loadPayments(active.id);
              loadStructures();
              showToast(
                "payment-delete-success",
                "Pagamento eliminato.",
                "",
                "success"
              );
            },
            () =>
              showToast(
                "payment-delete-error",
                "Errore nell'eliminazione del pagamento.",
                "",
                "danger"
              )
          ),
      });
    },
    [active, loadPayments, loadStructures, showToast]
  );

  const toFile: UploadProps["onChange"] = () => {};

  const captureFile =
    (setter: (f: File | null) => void): UploadProps["beforeUpload"] =>
    (file) => {
      setter(file as unknown as File);
      return false;
    };

  const structureColumns = [
    {
      title: "Struttura",
      key: "name",
      render: (_: unknown, s: ManagedStructure) => (
        <div>
          <div className="fw-bold">{s.name}</div>
          <small className="text-muted">
            {s.city} {s.province ? `(${s.province})` : ""}
          </small>
        </div>
      ),
    },
    {
      title: "Dati fatturazione",
      key: "billing",
      render: (_: unknown, s: ManagedStructure) =>
        s.billing ? (
          <div>
            <div>{s.billing.name}</div>
            <small className="text-muted">P.IVA {s.billing.vatCode}</small>
          </div>
        ) : (
          <Tag color="default">Non presenti</Tag>
        ),
    },
    {
      title: "Abbonamento",
      key: "annualFee",
      render: (_: unknown, s: ManagedStructure) => {
        const total = paymentsTotal(s.payments);
        const fee = s.annualFee;
        if (fee == null) return <Tag color="default">Non impostato</Tag>;
        const covered = total >= fee;
        return (
          <div>
            <div className="fw-bold">{formatEuro(fee)}</div>
            <small className={covered ? "text-success" : "text-muted"}>
              Versato: {formatEuro(total)}
              {covered ? " ✓" : ""}
            </small>
          </div>
        );
      },
    },
    {
      title: "Pagamenti",
      key: "payments",
      render: (_: unknown, s: ManagedStructure) => {
        const list = s.payments || [];
        if (!list.length) return <Tag color="red">Nessun pagamento</Tag>;
        const last = [...list].sort((a, b) =>
          (b.paymentDate || "").localeCompare(a.paymentDate || "")
        )[0];
        return (
          <div>
            {statusTag(last.status)}
            <small className="text-muted d-block">
              Ultimo: {formatDate(last.paymentDate)}
            </small>
          </div>
        );
      },
    },
    {
      title: "Stato",
      key: "active",
      render: (_: unknown, s: ManagedStructure) => (
        <Switch
          checked={s.active}
          checkedChildren="Attiva"
          unCheckedChildren="Disattiva"
          onChange={(value) => handleToggleActive(s, value)}
        />
      ),
    },
    {
      title: "Blocco",
      key: "locked",
      render: (_: unknown, s: ManagedStructure) =>
        s.locked ? (
          <div>
            <Tag color="red" className="mb-1">
              Bloccato
            </Tag>
            <Popconfirm
              title={`Sbloccare ${s.name}?`}
              description="Il gestore potrà di nuovo accedere e il circolo tornerà visibile nell'app."
              okText="Sblocca"
              cancelText="Annulla"
              onConfirm={() => handleSetLock(s, false)}
            >
              <Button size="small" loading={savingLockId === s.id}>
                <FontAwesomeIcon icon={faLockOpen} className="me-2" />
                Sblocca
              </Button>
            </Popconfirm>
          </div>
        ) : (
          <Button
            size="small"
            danger
            loading={savingLockId === s.id}
            onClick={() => {
              setLockMessage("");
              setLockTarget(s);
            }}
          >
            <FontAwesomeIcon icon={faLock} className="me-2" />
            Blocca
          </Button>
        ),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_: unknown, s: ManagedStructure) => (
        <Button onClick={() => openDrawer(s)}>
          <FontAwesomeIcon icon={faGear} className="me-2" />
          Gestisci
        </Button>
      ),
    },
  ];

  const paymentColumns = [
    {
      title: "Data",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (value: string) => formatDate(value),
    },
    {
      title: "Periodo",
      dataIndex: "period",
      key: "period",
      render: (v: string) => v || "-",
    },
    {
      title: "Importo",
      dataIndex: "amount",
      key: "amount",
      render: (v?: number) => formatEuro(v),
    },
    {
      title: "Acconto",
      dataIndex: "isDeposit",
      key: "isDeposit",
      render: (v?: boolean) =>
        v ? <Tag color="blue">Acconto</Tag> : <Tag color="green">Saldo</Tag>,
    },
    {
      title: "Stato",
      dataIndex: "status",
      key: "status",
      render: (v: PaymentStatus) => statusTag(v),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (v: string) => v || "-",
    },
    {
      title: "Fattura",
      key: "invoice",
      render: (_: unknown, p: Payment) =>
        p.invoiceUrl ? (
          <Button
            size="small"
            onClick={() => openDocument(Management.getInvoiceUrl(p.id))}
          >
            <FontAwesomeIcon icon={faFileInvoice} />
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: "",
      key: "delete",
      render: (_: unknown, p: Payment) => (
        <Button danger size="small" onClick={() => handleDeletePayment(p)}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      ),
    },
  ];

  return (
    <Container fluid="md" className="mt-5">
      <h4 className="mb-1">Pagamenti delle Strutture</h4>
      <p className="text-muted">
        Monitora i dati di fatturazione, lo stato dei pagamenti e dei contratti
        di ogni struttura.
      </p>
      <hr />

      <PlatformLockCard />

      {loading ? (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
        </div>
      ) : (
        <Table
          rowKey="id"
          dataSource={structures}
          columns={structureColumns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      )}

      <Modal
        title={lockTarget ? `Bloccare ${lockTarget.name}?` : ""}
        open={!!lockTarget}
        okText="Blocca circolo"
        cancelText="Annulla"
        okButtonProps={{
          danger: true,
          loading: !!lockTarget && savingLockId === lockTarget.id,
        }}
        onOk={() => lockTarget && handleSetLock(lockTarget, true, lockMessage)}
        onCancel={() => setLockTarget(null)}
      >
        <p>
          Il gestore verrà disconnesso dal gestionale e non potrà rientrare; il
          circolo sparirà dall'app (mappa, ricerca e preferiti). Puoi
          sbloccarlo in qualsiasi momento.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={300}
          showCount
          placeholder="Messaggio per il gestore (facoltativo)"
          value={lockMessage}
          onChange={(e) => setLockMessage(e.target.value)}
        />
      </Modal>

      <Drawer
        title={active ? `Gestione ${active.name}` : ""}
        open={!!active}
        onClose={closeDrawer}
        width={560}
      >
        {active && (
          <>
            <h6>Dati di fatturazione</h6>
            {active.billing ? (
              <ul className="list-unstyled">
                <li>
                  <strong>Ragione sociale:</strong> {active.billing.name}
                </li>
                <li>
                  <strong>P.IVA:</strong> {active.billing.vatCode}
                </li>
                <li>
                  <strong>PEC:</strong> {active.billing.pec}
                </li>
                <li>
                  <strong>Codice univoco:</strong> {active.billing.uniCode}
                </li>
              </ul>
            ) : (
              <Tag color="default">Dati di fatturazione non presenti</Tag>
            )}

            <hr />
            <h6>Contratto</h6>
            <div className="d-flex align-items-center gap-2 mb-2">
              {active.contractUrl ? (
                <Button
                  onClick={() =>
                    openDocument(Management.getContractUrl(active.id))
                  }
                >
                  <FontAwesomeIcon icon={faFileContract} className="me-2" />
                  Visualizza contratto
                </Button>
              ) : (
                <Tag color="red">Nessun contratto</Tag>
              )}
            </div>
            <Upload
              beforeUpload={captureFile(setContractFile)}
              onChange={toFile}
              maxCount={1}
              fileList={
                contractFile
                  ? ([
                      { uid: "contract", name: contractFile.name },
                    ] as UploadFile[])
                  : []
              }
              onRemove={() => setContractFile(null)}
            >
              <Button icon={<FontAwesomeIcon icon={faUpload} />}>
                Seleziona contratto (PDF/immagine)
              </Button>
            </Upload>
            {contractFile && (
              <Button
                type="primary"
                className="mt-2"
                loading={savingContract}
                onClick={handleUploadContract}
              >
                Carica contratto
              </Button>
            )}

            <hr />
            <h6>Abbonamento</h6>
            <div
              className="d-flex align-items-end gap-2 mb-2"
              style={{ maxWidth: 360 }}
            >
              <div style={{ flex: 1 }}>
                <label className="text-muted small d-block mb-1">
                  Costo annuale (€)
                </label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={annualFeeInput}
                  onChange={(e) => setAnnualFeeInput(e.target.value)}
                  placeholder="Es. 1200.00"
                />
              </div>
              <Button
                type="primary"
                loading={savingFee}
                onClick={handleSaveAnnualFee}
              >
                Salva
              </Button>
            </div>
            {(() => {
              const total = paymentsTotal(payments);
              const fee = active.annualFee;
              if (fee == null)
                return (
                  <small className="text-muted">
                    Imposta il costo per calcolare la copertura.
                  </small>
                );
              const covered = total >= fee;
              return (
                <small className={covered ? "text-success" : "text-muted"}>
                  Versato {formatEuro(total)} su {formatEuro(fee)}{" "}
                  {covered
                    ? "— Saldato ✓"
                    : `— Mancano ${formatEuro(fee - total)}`}
                </small>
              );
            })()}

            <hr />
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Pagamenti</h6>
              <Button
                type="primary"
                onClick={() => {
                  setInvoiceFile(null);
                  form.resetFields();
                  setPaymentModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Segna pagamento
              </Button>
            </div>
            <Table
              className="mt-3"
              rowKey="id"
              size="small"
              loading={paymentsLoading}
              dataSource={payments}
              columns={paymentColumns}
              pagination={false}
              scroll={{ x: true }}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Segna un pagamento"
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        onOk={() => form.submit()}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={savingPayment}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreatePayment}
          initialValues={{ isDeposit: false }}
        >
          <Form.Item
            label="Data pagamento"
            name="paymentDate"
            rules={[{ required: true, message: "Inserisci la data" }]}
          >
            <Input type="date" />
          </Form.Item>
          <Form.Item label="Periodo di riferimento" name="period">
            <Input placeholder="Es. Giugno 2026" />
          </Form.Item>
          <Form.Item label="Importo (€)" name="amount">
            <Input type="number" min={0} step="0.01" placeholder="0.00" />
          </Form.Item>
          <Form.Item
            label="Acconto"
            name="isDeposit"
            valuePropName="checked"
            tooltip="Attiva se questo pagamento è un acconto. Lo stato Pagato/In attesa è calcolato automaticamente in base alla copertura del costo abbonamento annuale."
          >
            <Switch checkedChildren="Sì" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item label="Note" name="note">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
          <Form.Item label="Fattura (opzionale, PDF/immagine)">
            <Upload
              beforeUpload={captureFile(setInvoiceFile)}
              onChange={toFile}
              maxCount={1}
              fileList={
                invoiceFile
                  ? ([
                      { uid: "invoice", name: invoiceFile.name },
                    ] as UploadFile[])
                  : []
              }
              onRemove={() => setInvoiceFile(null)}
            >
              <Button icon={<FontAwesomeIcon icon={faUpload} />}>
                Seleziona fattura
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
}
